import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { ModuleTypes } from "@/store/ModuleTypes";
import {
  getToday,
  getUnreadMessageCount,
  requestFanAccess
} from "@/helpers/api";
import BasicCardTemplate from "@/views/Today/Cards/BasicCardTemplate.vue";
import NoPlayersListedCard from "@/views/Today/Cards/NoPlayersListedCard.vue";
import SmallEventCard from "@/views/Today/Cards/SmallEventCard.vue";
import UnpaidFeesCard from "@/views/Today/Cards/UnpaidFeesCard.vue";
import LateFeesCard from "@/views/Today/Cards/LateFeesCard.vue";
import UnreadMessagesCard from "@/views/Today/Cards/UnreadMessagesCard.vue";
import PendingInvitesCard from "@/views/Today/Cards/PendingInvitesCard.vue";
import NoUpcomingEventsCard from "@/views/Today/Cards/NoUpcomingEventsCard.vue";
import UnreadChatCard from "@/views/Today/Cards/UnreadChatCard.vue";
import CreateOrganizationCard from "@/views/Today/Cards/CreateOrganizationCard.vue";
import MutationTypes from "@/store/MutationTypes";
import AlertCard from "@/views/Today/Cards/AlertCard.vue";
import NoNotificationsCard from "@/views/Today/Cards/NoNotificationsCard.vue";

@Module({ namespaced: true, name: ModuleTypes.TODAY })
export default class TodayModule extends VuexModule {
  data: any = null;
  error: null | string = null;
  loading = false;
  unreadMessageCount = 0;
  teamBotAlerts = [
    {
      html: "<div>Test Alert</div><span style='color: blue'>Click</span>"
    }
  ];

  get isLoading() {
    return this.loading;
  }

  get teamBotAlertCards() {
    return this.teamBotAlerts.map((alert: any) => {
      return {
        cardTemplate: BasicCardTemplate,
        card: AlertCard,
        props: {
          html: alert.html
        },
        templateProps: {
          rows: 1
        }
      };
    });
  }

  get noUpcomingEventsCard() {
    return this.hasEvents
      ? [
          {
            cardTemplate: BasicCardTemplate,
            card: NoUpcomingEventsCard
          }
        ]
      : [];
  }

  get remainingEvents() {
    const events = this.todaysEvents;

    return events.map((te: any) => {
      return {
        cardTemplate: BasicCardTemplate,
        card: SmallEventCard,
        props: {
          location: te.location,
          player: te.player,
          invite: te.invite,
          event: te.event
        },
        templateProps: {
          rows: 4
        }
      };
    });
  }

  get unpaidFeesCard() {
    return this.data.unpaidFeeCount > 0 &&
      this.data.unpaidLateFeeCount < this.data.unpaidFeeCount
      ? [
          {
            cardTemplate: BasicCardTemplate,
            card: UnpaidFeesCard,
            props: { unpaidFeeCount: this.data.unpaidFeeCount }
          }
        ]
      : [];
  }

  get lateFeesCard() {
    return this.data.unpaidLateFeeCount > 0
      ? [
          {
            cardTemplate: BasicCardTemplate,
            card: LateFeesCard,
            props: {
              unpaidLateFeeCount: this.data.unpaidLateFeeCount
            }
          }
        ]
      : [];
  }

  get unreadChatCard() {
    return this.unreadMessageCount > 0
      ? [
          {
            cardTemplate: BasicCardTemplate,
            card: UnreadChatCard,
            props: { unreadChatCount: this.unreadMessageCount }
          }
        ]
      : [];
  }

  get unreadMessagesCard() {
    return this.data.unreadMessagesCount > 0
      ? [
          {
            cardTemplate: BasicCardTemplate,
            card: UnreadMessagesCard,
            props: { unreadMessageCount: this.data.unreadMessagesCount }
          }
        ]
      : [];
  }

  get pendingInvitesCard() {
    return this.data.pendingPlayerInviteCount > 0
      ? [
          {
            cardTemplate: BasicCardTemplate,
            card: PendingInvitesCard,
            props: {
              pendingPlayerInviteCount: this.data.pendingPlayerInviteCount
            }
          }
        ]
      : [];
  }

  get noNotificationsCard() {
    return [
      {
        cardTemplate: BasicCardTemplate,
        card: NoNotificationsCard
      }
    ];
  }

  get noPlayersListedCard() {
    return [
      {
        cardTemplate: BasicCardTemplate,
        card: NoPlayersListedCard
      }
    ];
  }

  get createOrganizationCard() {
    return [
      {
        cardTemplate: BasicCardTemplate,
        card: CreateOrganizationCard
      }
    ];
  }

  get hasEvents() {
    return (
      this.data && this.data.todaysEvents && this.data.todaysEvents.length > 0
    );
  }

  get todaysEvents() {
    return this.hasEvents ? this.data.todaysEvents : [];
  }

  get hasNotifications() {
    return (
      [
        ...this.lateFeesCard,
        ...this.unpaidFeesCard,
        ...this.unreadMessagesCard,
        ...this.unreadChatCard,
        ...this.pendingInvitesCard
      ].length > 0
    );
  }

  get todayCards() {
    if (!this.data) {
      return null;
    }

    const cards = [];
    // cards.push(...this.teamBotAlertCards)
    cards.push(...this.remainingEvents);
    // cards.push(...this.noUpcomingEventsCard);
    cards.push(...this.lateFeesCard);
    cards.push(...this.unpaidFeesCard);
    cards.push(...this.unreadMessagesCard);
    cards.push(...this.unreadChatCard);
    cards.push(...this.pendingInvitesCard);
    if (!this.hasNotifications) {
      cards.push(...this.noNotificationsCard);
    }

    return cards;
  }

  get navNotifications() {
    const arr: Array<string> = [];
    if (this.data) {
      if (this.data.unreadMessagesCount > 0) {
        arr.push("email");
      }
      if (this.unreadMessageCount > 0) {
        arr.push("chat");
      }
      if (this.data.pendingPlayerInviteCount > 0) {
        arr.push("calendar");
      }
      if (this.data.unpaidFeeCount > 0 || this.data.unpaidLateFeeCount > 0) {
        arr.push("payments");
      }
    }

    return arr;
  }
  buildBasicCard = (card: any, props: any) => {
    return {
      cardTemplate: BasicCardTemplate,
      card: card,
      props: props
    };
  };

  @Mutation
  setDataObject(data: any) {
    this.data = data;
  }

  @Mutation
  setUnreadMessageCount(int: number) {
    this.unreadMessageCount = int;
  }

  @Mutation
  setLoading(val: boolean) {
    this.loading = val;
  }

  @Action
  async fetchData() {
    let data;
    let unreadMessagesCount;
    try {
      this.context.commit("setLoading", true);
      data = await getToday();
      unreadMessagesCount = await getUnreadMessageCount();
      this.context.commit("setDataObject", data);
      this.context.commit("setUnreadMessageCount", unreadMessagesCount);

      this.context.commit(
        MutationTypes.SET_NAV_NOTIFICATIONS,
        this.navNotifications,
        { root: true }
      );
    } catch (e) {
      this.context.commit("setLoading", false);
      this.error = "Error loading today's information.";
    }
    this.context.commit("setLoading", false);
  }

  @Action
  async requestFanAccess({ email, playerName }: any) {
    return await requestFanAccess(email, playerName);
  }
}
